// Shared internals for the sourceEvents/ module. Defines small atoms — DetailField,
// CollapsibleSection, stripPort, IpLink — that the SourceEventRenderer +
// RawEventJsonPanel components both rely on. Mirrors the equivalent locals defined
// inline in AlertsPage.jsx, but lives here so the Recent Events page (and a
// future migration of AlertsPage / TestRunResultsPage — see CLAUDE.md three-way
// parity rule) can share a single source of truth.

import React, { useState } from 'react';
import { ChevronRightIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import CopyButton from '../CopyButton';
import { normalizeIsoUtc, resolveTz } from '../../utils/timeFormat';

// Render label + value as a compact two-line "field" inside a detail grid.
// `mono` switches the value to a monospace font, useful for IDs / IPs / UPNs.
export function DetailField({ label, children, mono }) {
  return (
    <div className="space-y-0.5">
      <div className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">{label}</div>
      <div className={`text-sm ${mono ? 'font-mono' : ''} text-gray-200`}>{children}</div>
    </div>
  );
}

// Collapsible panel with a chevron toggle. Supports both uncontrolled (set
// `defaultOpen`) and controlled (set `open` + `onToggle`) modes. Controlled mode
// is useful when the parent needs to fire side effects on expand — e.g.
// RawEventJsonPanel lazy-fetches the spilled blob the first time it's opened.
// `headerExtra` is an optional ReactNode rendered inline to the right of the
// title (only when expanded) — used by RawEventJsonPanel for the visible Copy
// button, matching AlertsPage's CollapsibleJson pattern.
export function CollapsibleSection({ title, defaultOpen = false, open: controlledOpen, onToggle, headerClassName = 'text-gray-400', headerExtra, children }) {
  const [innerOpen, setInnerOpen] = useState(defaultOpen);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : innerOpen;
  const toggle = () => {
    const next = !open;
    if (isControlled) {
      if (onToggle) onToggle(next);
    } else {
      setInnerOpen(next);
    }
  };
  return (
    <div>
      <div className="flex items-center gap-1.5 select-none">
        <button
          type="button"
          onClick={toggle}
          className={`flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide ${headerClassName} hover:text-gray-200 transition-colors`}
        >
          {open
            ? <ChevronDownIcon className="h-3 w-3" />
            : <ChevronRightIcon className="h-3 w-3" />}
          <span>{title}</span>
        </button>
        {open && headerExtra}
      </div>
      {open && (
        <div className="mt-2">
          {children}
        </div>
      )}
    </div>
  );
}

// IPv4/IPv6 addresses sometimes arrive with a trailing port suffix (e.g. "1.2.3.4:443"
// or "[::1]:443"). For display purposes we strip the port. IPv6 in brackets keeps
// only the bracketed content, then strips brackets for cleanliness.
export function stripPort(ip) {
  if (!ip) return '';
  const s = String(ip).trim();
  if (s.startsWith('[')) {
    const end = s.indexOf(']');
    return end > 0 ? s.slice(1, end) : s;
  }
  // IPv4 with port — single colon, no other colons (IPv6 without brackets is ambiguous).
  if (s.indexOf(':') > 0 && s.indexOf(':') === s.lastIndexOf(':')) {
    return s.slice(0, s.indexOf(':'));
  }
  return s;
}

// IP rendered as a hyperlink to iplocation.net + a hover-reveal CopyButton.
// Falls back to a muted em-dash when the IP is missing.
export function IpLink({ ip }) {
  const clean = stripPort(ip);
  if (!clean) return <span className="text-gray-500">—</span>;
  return (
    <span className="group inline-flex items-center gap-0.5">
      <a
        href={`https://www.iplocation.net/ip-lookup?query=${encodeURIComponent(clean)}`}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-400 hover:text-blue-300 hover:underline"
        onClick={(e) => e.stopPropagation()}
      >
        {clean}
      </a>
      <CopyButton text={clean} />
    </span>
  );
}

// Render an absolute UTC ISO timestamp in the user-selected IANA timezone, with
// a fixed 'Month Day, Year HH:MM:SS' shape and 24h clock. Returns '—' on missing.
export function FormatTs({ ts, tzIana }) {
  if (!ts) return '—';
  try {
    // 'Local' carries an empty tzIana — resolve to the browser zone; an empty
    // timeZone option throws in toLocaleString and would fall back to raw UTC.
    const zone = resolveTz(tzIana);
    // normalizeIsoUtc: append 'Z' when no zone is present (backend writes UTC) and
    // truncate >3-digit fractional seconds — Firefox/Safari reject them as Invalid
    // Date, which would drop us into the catch and render the raw string.
    return new Date(normalizeIsoUtc(ts)).toLocaleString('en-US', {
      timeZone: zone,
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
    });
  } catch {
    return ts;
  }
}
